import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import Seo from "../Seo";
import { fetchSymbolData } from "../dataSource";
import SentimentWidget from "./SentimentWidget";
import IdeasPanel from "./IdeasPanel";
import ArticlesPanel from "./ArticlesPanel";
import PanelErrorBoundary from "./PanelErrorBoundary";
import DevPanel from "./DevPanel";

function BackButton() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <button
      onClick={() => navigate("/", { state: { scrollTo: "news-sentiment" } })}
      aria-label={t("common.goBack")}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 40, height: 40, borderRadius: "50%",
        background: "var(--chip-bg, #fff)", border: "none",
        color: "var(--text-strong, #3c4043)",
        boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
        cursor: "pointer", marginBottom: 16, marginTop: 12,
        transition: "box-shadow 0.15s, background 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.26)"; e.currentTarget.style.background = "var(--row-hover, #f5f5f5)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.18)"; e.currentTarget.style.background = "var(--chip-bg, #fff)"; }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
      </svg>
    </button>
  );
}

function PredictionPage({ title, content, canonical, categoryKey, categoryPath, symbol }) {
  const { t } = useTranslation();
  const [sentimentData, setSentimentData] = useState(null);
  const [intraday, setIntraday] = useState(null);
  const [thresh] = useState(0.15);
  const [selectedIdea, setSelectedIdea] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    fetchSymbolData("sentiment", symbol)
      .then(setSentimentData)
      .catch(() => setSentimentData(null));
    fetchSymbolData("intraday", symbol)
      .then(setIntraday)
      .catch(() => setIntraday(null));
  }, [symbol]);

  const category = categoryKey ? t(categoryKey) : null;
  const baseDesc = t("prediction.descTemplate", { title });
  const latest   = sentimentData?.latest;

  const description = latest
    ? `${title} AI sentiment analysis — ${latest.label_d7} (D7: ${latest.score_d7 >= 0 ? "+" : ""}${latest.score_d7.toFixed(3)}, D3: ${latest.score_d3 >= 0 ? "+" : ""}${latest.score_d3.toFixed(3)}). ${baseDesc}`
    : baseDesc;

  const breadcrumbs = [
    { name: t("common.home"), path: "/" },
    ...(category && categoryPath ? [{ name: category, path: categoryPath }] : []),
    { name: `${title} ${t("prediction.breadcrumbLabel")}`, path: canonical || "/" },
  ];

  const schema = latest
    ? {
        "@context": "https://schema.org",
        "@type": "Dataset",
        name: `${title} AI Sentiment Analysis`,
        description,
        dateModified: sentimentData.updated,
        variableMeasured: [
          { "@type": "PropertyValue", name: "7-Day Sentiment Score", value: latest.score_d7 },
          { "@type": "PropertyValue", name: "7-Day Signal",          value: latest.label_d7 },
          { "@type": "PropertyValue", name: "3-Day Sentiment Score", value: latest.score_d3 },
          { "@type": "PropertyValue", name: "3-Day Signal",          value: latest.label_d3 },
        ],
      }
    : null;

  return (
    <>
      <Seo
        title={`${title} ${t("prediction.titleSuffix")}`}
        description={description}
        canonical={canonical}
        breadcrumbs={breadcrumbs}
        schema={schema}
      />
      <Container>
        <BackButton />
        <h1 style={{ marginTop: 0 }}>{title}</h1>
        <p style={{ color: "var(--text-secondary)", fontSize: 15, marginTop: -8, marginBottom: 20 }}>
          {t("prediction.dailySubtitle")}
        </p>
        {sentimentData?.latest?.summary && (() => {
          const s = sentimentData.latest.summary;
          const text = typeof s === "object" ? (s[i18n.language] || s.en) : s;
          return text ? (
            <p style={{ fontSize: 14, lineHeight: 1.7, color: "var(--text-body)", maxWidth: 720, margin: "0 auto 24px", textAlign: "left" }}>
              {text}
            </p>
          ) : null;
        })()}
        {symbol ? (
          <>
            <PanelErrorBoundary name="Sentiment chart">
              <SentimentWidget data={sentimentData} intraday={intraday} thresh={thresh} selectedIdea={selectedIdea} />
            </PanelErrorBoundary>
            <div style={{ marginTop: 32 }}>
              <PanelErrorBoundary name="Ideas panel">
                <IdeasPanel symbol={symbol} selected={selectedIdea} onSelect={setSelectedIdea}
                            chartDates={(sentimentData?.candles || []).map(c => c.date)} />
              </PanelErrorBoundary>
            </div>
            <div style={{ marginTop: 32 }}>
              <PanelErrorBoundary name="Articles panel">
                <ArticlesPanel symbol={symbol} />
              </PanelErrorBoundary>
            </div>
            <DevPanel symbol={symbol} date={sentimentData?.latest?.date} />
          </>
        ) : (
          <p>{content}</p>
        )}
      </Container>
    </>
  );
}

export default PredictionPage;
